import type { Extension } from "@codemirror/state";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PLUGIN_CATALOG, type PluginId, type PluginListing } from "@ide/shared";
import { FULL_EDITION } from "@/desktop/edition";
import { useTheme } from "@/lib/theme";
import { del, errorMessage, get, put } from "@/lib/api";
import { importedEditorTheme } from "@/editor/theme";
import { PLUGIN_RUNTIME, type PluginRuntime } from "./registry";
import {
  applyImportedUi,
  importedSnippets,
  useVsCodeContent,
  vscodeKeys,
  type VsCodeContent,
} from "./vscode-imported";
import { useIconTheme } from "@/components/file-icons";

export const pluginKeys = { all: ["plugins"] as const };

export const usePlugins = (enabled = true) =>
  useQuery({
    queryKey: pluginKeys.all,
    queryFn: async () => (await get<{ items: PluginListing[] }>("/plugins")).items,
    staleTime: 60_000,
    enabled,
  });

/** Temas mudam o editor (e às vezes a IDE inteira): só um fica ligado por vez. */
const isTheme = (id: string) =>
  PLUGIN_CATALOG.find((p) => p.id === id)?.permissions.some(
    (perm) => perm === "editor.theme" || perm === "ui.theme",
  ) ?? false;

/** Instala/liga/desliga/remove com atualização otimista (a lista reage na hora). */
export function usePluginAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: PluginId;
      action: "install" | "enable" | "disable" | "remove";
    }) => {
      if (action === "remove") return del(`/me/plugins/${id}`);
      if ((action === "install" || action === "enable") && isTheme(id)) {
        // O tema trazido do VS Code vence os do Kompil: escolher um do Kompil desliga o importado
        // (senão a troca não aparece e parece que o tema "não carregou").
        if (qc.getQueryData<VsCodeContent>(vscodeKeys.content)?.theme)
          await del("/me/vscode-extensions/theme");
        const others = (qc.getQueryData<PluginListing[]>(pluginKeys.all) ?? []).filter(
          (p) => p.id !== id && p.installed && p.enabled && isTheme(p.id),
        );
        for (const o of others) await put(`/me/plugins/${o.id}`, { enabled: false });
      }
      return put(`/me/plugins/${id}`, { enabled: action !== "disable" });
    },
    onMutate: async ({ id, action }) => {
      await qc.cancelQueries({ queryKey: pluginKeys.all });
      // Troca na hora: o tema importado sai junto (o servidor confirma em seguida).
      if ((action === "install" || action === "enable") && isTheme(id))
        qc.setQueryData<VsCodeContent>(vscodeKeys.content, (c) => (c ? { ...c, theme: null } : c));
      const previous = qc.getQueryData<PluginListing[]>(pluginKeys.all);
      qc.setQueryData<PluginListing[]>(pluginKeys.all, (list) =>
        list?.map((p) =>
          p.id !== id
            ? // Ligar um tema desliga os outros temas.
              (action === "install" || action === "enable") && isTheme(id) && isTheme(p.id)
              ? { ...p, enabled: false }
              : p
            : {
                ...p,
                installed: action !== "remove",
                enabled: action === "install" || action === "enable",
                installs: p.installs + (action === "install" ? 1 : action === "remove" && p.enabled ? -1 : 0),
              },
        ),
      );
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(pluginKeys.all, ctx.previous);
      toast.error(errorMessage(err));
    },
    onSettled: (_data, _err, { id }) => {
      void qc.invalidateQueries({ queryKey: pluginKeys.all });
      if (isTheme(id)) void qc.invalidateQueries({ queryKey: vscodeKeys.list });
    },
  });
}

/** Runtimes dos plugins instalados e ligados (plugins são do produto completo: app desktop). */
export function useActivePlugins(): Array<{ id: PluginId; runtime: PluginRuntime }> {
  const { data } = usePlugins(FULL_EDITION);
  return useMemo(
    () =>
      (FULL_EDITION ? (data ?? []) : [])
        .filter((p) => p.installed && p.enabled)
        .map((p) => ({ id: p.id as PluginId, runtime: PLUGIN_RUNTIME[p.id as PluginId] }))
        .filter((p) => p.runtime),
    [data],
  );
}

/**
 * Extensões de editor e tema dos plugins ativos. Extensões carregadas sob demanda (Vim)
 * entram quando chegam; até lá o editor funciona sem elas.
 */
export function useEditorPlugins(dark: boolean) {
  const active = useActivePlugins();
  const { data: vscode } = useVsCodeContent(FULL_EDITION);
  const [loaded, setLoaded] = useState<{ key: string; extensions: Extension[] }>({ key: "", extensions: [] });
  const key = active.map((p) => p.id).join(",");

  useEffect(() => {
    let alive = true;
    void Promise.all(active.map((p) => p.runtime.editor?.() ?? null)).then((exts) => {
      if (alive) setLoaded({ key, extensions: exts.filter((e): e is Extension => e !== null) });
    });
    return () => {
      alive = false;
    };
    // `key` resume a lista de plugins ativos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Memoizado: um tema novo a cada render reconfiguraria o editor à toa.
  // Tema importado do VS Code (se a pessoa ligou um) vence os temas do Kompil.
  const themeId = active.find((p) => p.runtime.theme)?.id ?? null;
  const imported = vscode?.theme ?? null;
  const theme = useMemo(
    () =>
      imported
        ? importedEditorTheme(imported)
        : themeId
          ? (PLUGIN_RUNTIME[themeId].theme?.(dark) ?? null)
          : null,
    [imported, themeId, dark],
  );
  const vsSnippets = useMemo(() => importedSnippets(vscode?.snippets ?? []), [vscode?.snippets]);
  const ready = loaded.key === key;
  const extensions = useMemo(
    () => [...(ready ? loaded.extensions : []), ...vsSnippets],
    [ready, loaded.extensions, vsSnippets],
  );
  return { extensions, theme, key: `${key}|${vscode?.snippets.length ?? 0}|${imported?.label ?? ""}` };
}

/**
 * Ícones de arquivo coloridos (como os do VS Code) são o padrão; só ficam monocromáticos se a
 * pessoa instalou o plugin "Ícones de arquivo" e o desligou.
 */
export function useColoredIcons(): boolean {
  const { data } = usePlugins(FULL_EDITION);
  const plugin = (FULL_EDITION ? (data ?? []) : []).find((p) => p.id === "kompil.file-icons");
  const colored = !(plugin?.installed && !plugin.enabled);
  // Os ícones de fora do editor (chat, anexos) leem daqui.
  useEffect(() => useIconTheme.setState({ colored }), [colored]);
  return colored;
}

const UI_THEME_KEY = "kompil.uiTheme";

/** Classes do tema de interface ligado ("" = padrão preto e branco). */
const uiThemeOf = (list: PluginListing[] | undefined) =>
  (list ?? [])
    .filter((p) => p.installed && p.enabled)
    .map((p) => PLUGIN_RUNTIME[p.id as PluginId]?.uiTheme)
    .find(Boolean) ?? "";

/** Tema claro só com o plugin "Tema claro"; o padrão (e sem conta) é o escuro. */
const modeOf = (list: PluginListing[] | undefined): "light" | "dark" =>
  (list ?? []).some((p) => p.installed && p.enabled && PLUGIN_RUNTIME[p.id as PluginId]?.mode === "light")
    ? "light"
    : "dark";

/** Para quem precisa reagir à troca de tema (ex.: o terminal, que não usa as variáveis CSS). */
export const useUiThemeClasses = () => uiThemeOf(usePlugins().data);

/**
 * Aplica no <html> as classes do tema de interface ativo (ui.theme) e guarda para o
 * theme-init.js aplicar antes do primeiro paint na próxima visita (sem "piscar").
 */
export function useUiTheme(enabled: boolean) {
  const { data } = usePlugins(enabled);
  const { data: vscode, isFetched } = useVsCodeContent(enabled);
  const imported = enabled ? (vscode?.theme ?? null) : null;
  const classes = imported ? "theme-color-code ui-theme-vscode" : enabled ? uiThemeOf(data) : "";
  const mode = imported ? (imported.dark ? "dark" : "light") : enabled ? modeOf(data) : "dark";
  const ready = !enabled || (data !== undefined && isFetched);
  useEffect(() => {
    if (!ready) return;
    applyImportedUi(imported);
    const root = document.documentElement;
    if (useTheme.getState().theme !== mode) useTheme.getState().setTheme(mode);
    for (const c of [...root.classList])
      if (c === "theme-color-code" || c.startsWith("ui-theme-")) root.classList.remove(c);
    for (const c of classes.split(" ").filter(Boolean)) root.classList.add(c);
    // Tema de uma extensão do VS Code: o motor usa o mesmo nas vistas embutidas (Claude, ferramentas).
    if (imported) root.dataset.vscodeTheme = imported.label;
    else delete root.dataset.vscodeTheme;
    try {
      if (classes) localStorage.setItem(UI_THEME_KEY, classes);
      else localStorage.removeItem(UI_THEME_KEY);
    } catch {
      // armazenamento bloqueado: o tema só não é lembrado entre visitas
    }
  }, [classes, mode, ready, imported]);
}
